import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { QUOTES_BY_LANG, tr } from "@/lib/i18n";
import { useAuth } from "@/context/AuthContext";

const SESSION_KEY = "leo_quote_seen";

export default function QuoteGate() {
  const { user, lang } = useAuth();
  const navigate = useNavigate();
  const t = (k) => tr(k, lang);
  const [entering, setEntering] = useState(false);
  const quotes = QUOTES_BY_LANG[lang] || QUOTES_BY_LANG.pl;

  // Pick ONE random quote per mount so the ritual feels fresh each time
  // the actor opens the app.
  const q = useMemo(() => quotes[Math.floor(Math.random() * quotes.length)], [quotes]);

  // Skip the gate if already crossed this session (e.g. user navigated back
  // via the browser). Never trap the actor in a loop.
  useEffect(() => {
    try {
      if (sessionStorage.getItem(SESSION_KEY) === "1") {
        const dest = user?.needs_first_onboarding ? "/leo?src=first-onboarding" : "/dashboard";
        navigate(dest, { replace: true });
      }
    } catch { /* private mode: just show the gate */ }
  }, [navigate, user]);

  const enter = () => {
    if (entering) return;
    setEntering(true);
    try { sessionStorage.setItem(SESSION_KEY, "1"); } catch { /* ignore */ }
    // Spec §2: first-onboarding gate always wins over the normal /dashboard
    // path. The App-level guard would also redirect on other entry points,
    // but doing it here avoids one extra hop for the QuoteGate flow.
    const dest = !user
      ? "/login"
      : (user?.needs_first_onboarding ? "/leo?src=first-onboarding" : "/dashboard");
    setTimeout(() => navigate(dest, { replace: true }), 320);
  };

  // Space / Enter also enters the app (accessibility + keyboard actors)
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === " " || e.key === "Enter") { e.preventDefault(); enter(); }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [entering]);

  return (
    <AnimatePresence>
      {!entering && (
        <motion.div
          data-testid="quote-gate"
          role="button"
          tabIndex={0}
          onClick={enter}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.6 }}
          className="fixed inset-0 z-[9000] bg-stone-950 cursor-pointer select-none flex items-center justify-center px-6"
        >
          {/* subtle amber vignette */}
          <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(ellipse_at_center,_rgba(217,119,6,0.08),_transparent_60%)]"/>

          <div className="relative max-w-3xl w-full text-center">
            <motion.div
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.4, duration: 0.6 }}
              className="mono text-[10px] tracking-[0.5em] text-amber-500/80 uppercase"
            >
              LEO — The Actor's Career System
            </motion.div>

            <motion.blockquote
              initial={{ opacity: 0, y: 24 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.7, duration: 0.9 }}
              className="mt-10 serif italic text-stone-100 text-3xl sm:text-4xl md:text-5xl leading-[1.15]"
              data-testid="quote-gate-text"
            >
              „{q.text}"
            </motion.blockquote>

            <motion.footer
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 1.3, duration: 0.6 }}
              className="mt-8 mono text-xs tracking-[0.4em] uppercase text-amber-500/80"
              data-testid="quote-gate-author"
            >
              — {q.author}
            </motion.footer>

            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 1.8, duration: 0.8 }}
              className="mt-16"
            >
              <button
                data-testid="quote-gate-enter-btn"
                onClick={(e) => { e.stopPropagation(); enter(); }}
                className="mono text-[11px] tracking-[0.4em] uppercase text-stone-300 hover:text-amber-400 transition-colors border-b border-stone-700 hover:border-amber-500 pb-1.5 px-2"
              >
                {lang === "pl" ? "Kliknij, aby wejść" : "Tap to enter"}
              </button>
              <div className="mono text-[9px] tracking-[0.3em] uppercase text-stone-600 mt-4">
                {lang === "pl" ? "lub naciśnij spację" : "or press space"}
              </div>
            </motion.div>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
